import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO, { createBreadcrumbSchema, createFAQSchema, softwareApplicationSchema } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, CheckCircle, Zap, Image, ShoppingCart, Layers, Palette, Clock, Star, TrendingUp } from 'lucide-react';

const AIImageGeneratorShopify = () => {
  const faqs = [
    {
      question: "What image sizes are required for Shopify product listings?",
      answer: "Shopify recommends 2048x2048px square images for optimal display across all devices. Our AI generator creates high-resolution images that meet Shopify's requirements with options for product shots, lifestyle images, and collection banners."
    },
    {
      question: "Can I create product mockups for my Shopify store?",
      answer: "Absolutely! Our AI excels at generating realistic product mockups, lifestyle photography, and collection imagery. Perfect for apparel, accessories, home goods, beauty products, and more."
    },
    {
      question: "Are AI-generated images allowed on Shopify?",
      answer: "Yes, AI-generated images are fully allowed on Shopify for product photography and marketing. Our platform provides full commercial rights for all generated images, making them completely safe for your Shopify store."
    },
    {
      question: "How many Shopify product images can I create at once?",
      answer: "With our bulk generation feature, you can create hundreds of unique product images in minutes. Generate variations for A/B testing, seasonal themes, or complete product catalogs efficiently."
    },
    {
      question: "Can I integrate with my Shopify store directly?",
      answer: "While direct integration is coming soon, you can easily download your generated images and upload them to your Shopify store. Our images are optimized for Shopify's requirements out of the box."
    }
  ];

  const features = [
    {
      icon: <Image className="w-6 h-6" />,
      title: "Shopify-Optimized Sizes",
      description: "Auto-generate 2048x2048px square images that display perfectly on Shopify product pages and collections"
    },
    {
      icon: <ShoppingCart className="w-6 h-6" />,
      title: "E-commerce Ready",
      description: "Create professional product photos with white backgrounds, lifestyle shots, and collection imagery"
    },
    {
      icon: <Layers className="w-6 h-6" />,
      title: "Product Mockups",
      description: "Generate realistic mockups for t-shirts, mugs, phone cases, posters, and apparel products"
    },
    {
      icon: <Palette className="w-6 h-6" />,
      title: "Brand Consistency",
      description: "Maintain consistent visual branding across your entire Shopify product catalog"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Bulk Generation",
      description: "Generate 100+ unique product images in minutes instead of hours of photography"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Boost Conversions",
      description: "Professional images increase trust and can boost conversion rates by up to 30%"
    }
  ];

  const useCases = [
    "Dropshipping stores needing professional product images fast",
    "Print-on-demand sellers creating apparel and accessory mockups",
    "DTC brands launching new product lines",
    "Shopify Plus merchants scaling product photography",
    "Small businesses replacing expensive photo shoots",
    "Collection and category banner creation",
    "Seasonal campaign and promotion imagery",
    "Product variation images (colors, sizes, styles)"
  ];

  return (
    <>
      <SEO
        title="AI Image Generator for Shopify | Product Photos That Convert"
        description="Create stunning Shopify product photos with AI. Professional mockups, lifestyle images, collection banners in seconds. Boost conversions with beautiful product photography. Used by 5,000+ Shopify stores."
        keywords="ai image generator shopify, shopify product photos ai, shopify listing images, shopify store photography, shopify mockup generator, shopify seller tools, best images for shopify, shopify product photography ai, shopify dropshipping images, shopify print on demand"
        url="/ai-image-generator-shopify"
        jsonLd={[
          softwareApplicationSchema,
          createBreadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'AI Image Generator for Shopify', url: '/ai-image-generator-shopify' }
          ]),
          createFAQSchema(faqs)
        ]}
      />
      
      <Header />
      
      <main className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 via-emerald-500/5 to-background" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-4 bg-gradient-to-r from-green-500/20 to-emerald-500/20 text-foreground border-0">
                Trusted by 5,000+ Shopify Stores in the US
              </Badge>
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                AI Image Generator for Shopify
                <span className="block bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent drop-shadow-sm">
                  Product Photos That Convert
                </span>
              </h1>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Create stunning Shopify product images with AI. Professional mockups, lifestyle photography, 
                and collection banners in seconds. No photography skills or expensive equipment needed.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-gradient-to-r from-green-500 to-emerald-500 hover:opacity-90 text-white">
                  <Link to="/try-generator">
                    Create Shopify Images Free <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link to="/gallery">View Product Examples</Link>
                </Button>
              </div>
              
              {/* Trust Badges */}
              <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  <span>4.9/5 from 2,800+ reviews</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Full Commercial Rights</span>
                </div>
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-emerald-500" />
                  <span>Images in Seconds</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Why US Shopify Sellers Choose Our AI Generator
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {features.map((feature, index) => (
                <Card key={index} className="border border-transparent bg-gradient-to-br from-green-500/5 to-emerald-500/5 hover:from-green-500/10 hover:to-emerald-500/10 hover:border-green-500/20 transition-all shadow-sm hover:shadow-md">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-green-500/20 to-emerald-500/20 flex items-center justify-center text-foreground mb-4">
                      {feature.icon}
                    </div>
                    <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">
                Perfect For Every Shopify Store Type
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {useCases.map((useCase, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 rounded-lg bg-gradient-to-r from-green-500/5 to-emerald-500/5">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span>{useCase}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Cross-links */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-8">
              Selling on Other Platforms?
            </h2>
            <div className="flex flex-wrap justify-center gap-4 max-w-3xl mx-auto">
              <Button asChild variant="outline">
                <Link to="/ai-image-generator-for-etsy">AI for Etsy Sellers</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/ai-product-photography-amazon">AI for Amazon FBA</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/ai-image-generator-ebay">AI for eBay Sellers</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Frequently Asked Questions
            </h2>
            <div className="max-w-3xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <Card key={index} className="border-0 bg-gradient-to-br from-green-500/5 to-emerald-500/5">
                  <CardContent className="p-6">
                    <h3 className="font-semibold mb-2">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6">
                Start Creating Stunning Shopify Product Images Today
              </h2>
              <p className="text-xl text-muted-foreground mb-8">
                Join 5,000+ Shopify stores using AI to create professional product photos. 
                No photography skills, expensive equipment, or long photo shoots needed.
              </p>
              <Button asChild size="lg" className="bg-gradient-to-r from-green-500 to-emerald-500 hover:opacity-90 text-white">
                <Link to="/try-generator">
                  Generate Shopify Images Free <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </>
  );
};

export default AIImageGeneratorShopify;
